import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCurrentUser } from '../../store/slices/authSlice';
import { addNotification } from '../../store/slices/uiSlice';

import { API_URL } from '../../config/api';
import { fileForUpload } from '../../utils/uploadFilename';
import PresignedAvatar from '../common/PresignedAvatar';
import AdminRunScriptPanel from '../admin/AdminRunScriptPanel';

const formatDateOfBirth = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    contactNumber: '',
    profilePicture: '',
    organizationRoleName: '',
    reportsToRoleName: '',
    supervisorName: '',
    supervisorUsername: '',
    membersUnder: [],
    organizationId: '',
    globalOrganization: true,
    socialLinks: {
      whatsapp: '',
      facebook: '',
      instagram: '',
      linkedin: '',
      zoom: '',
      googleMeet: '',
      github: '',
      website: '',
      customLinks: []
    }
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [orgData, setOrgData] = useState({
    organizationName: '',
    organizationSlug: '',
    jiraSiteUrl: '',
  });
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'password', 'social' or 'organization'
  const [pictureMessage, setPictureMessage] = useState(null);
  useEffect(() => {
    fetchProfile();
    if (user?.role === 'ORG_ADMIN') {
      fetchOrganization();
    }
  }, [user?.role, token]); // Add token to dependency array to ensure it fetches when token is available

  const fetchOrganization = async () => {
    if (!token) return; // Wait until token is available
    try {
      const response = await axios.get(`${API_URL}/organizations/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOrgData({
        organizationName: response.data.organizationName || '',
        organizationSlug: response.data.organizationSlug || '',
        jiraSiteUrl: response.data.jiraSiteUrl || '',
      });
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = response.data;
      setProfileData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        dateOfBirth: data.dateOfBirth || data.dob || '',
        email: data.email || '',
        contactNumber: data.contactNumber || '',
        profilePicture: data.profilePicture || '',
        username: data.username || '',
        role: data.role || user?.role || '',
        organizationRoleName: data.organizationRoleName || '',
        reportsToRoleName: data.reportsToRoleName || '',
        supervisorName: data.supervisorName || '',
        supervisorUsername: data.supervisorUsername || '',
        membersUnder: Array.isArray(data.membersUnder) ? data.membersUnder : [],
        organizationId: data.organizationId || '',
        globalOrganization: Boolean(data.globalOrganization),
        socialLinks: {
          whatsapp: data.socialLinks?.whatsapp || '',
          facebook: data.socialLinks?.facebook || '',
          instagram: data.socialLinks?.instagram || '',
          linkedin: data.socialLinks?.linkedin || '',
          zoom: data.socialLinks?.zoom || '',
          googleMeet: data.socialLinks?.googleMeet || '',
          github: data.socialLinks?.github || '',
          website: data.socialLinks?.website || '',
          customLinks: data.socialLinks?.customLinks || [],
        }
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load profile'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomLink = () => {
    setProfileData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        customLinks: [...(prev.socialLinks.customLinks || []), { title: '', url: '' }]
      }
    }));
  };

  const handleUpdateCustomLink = (index, field, value) => {
    setProfileData(prev => {
      const newCustomLinks = [...(prev.socialLinks.customLinks || [])];
      newCustomLinks[index] = { ...newCustomLinks[index], [field]: value };
      return {
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          customLinks: newCustomLinks
        }
      };
    });
  };

  const handleRemoveCustomLink = (index) => {
    setProfileData(prev => {
      const newCustomLinks = [...(prev.socialLinks.customLinks || [])];
      newCustomLinks.splice(index, 1);
      return {
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          customLinks: newCustomLinks
        }
      };
    });
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPictureMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPictureMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    try {
      setLoading(true);
      setPictureMessage(null);
      const formData = new FormData();
      formData.append('file', fileForUpload(file));

      const uploadResponse = await axios.post(`${API_URL}/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = uploadResponse.data.fileUrl;
      setProfileData({ ...profileData, profilePicture: fileUrl });
      setPictureMessage({ type: 'success', text: 'Profile picture uploaded successfully' });
    } catch (error) {
      console.error('Failed to upload image:', error);
      setPictureMessage({ type: 'error', text: 'Failed to upload profile picture' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const canEditDob = profileData.role === 'ORG_ADMIN' || profileData.role === 'ADMIN';
      const response = await axios.put(`${API_URL}/profile`, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        profilePicture: profileData.profilePicture,
        socialLinks: profileData.socialLinks,
        ...(canEditDob ? { dateOfBirth: profileData.dateOfBirth || '' } : {}),
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Update Redux store with new user data
      dispatch(getCurrentUser());
      dispatch(addNotification({
        type: 'success',
        message: 'Profile updated'
      }));
    } catch (error) {
      console.error('Failed to update profile:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to update profile'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      dispatch(addNotification({
        type: 'error',
        message: 'New password and confirm password do not match'
      }));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      dispatch(addNotification({
        type: 'error',
        message: 'Password must be at least 6 characters long'
      }));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/profile/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      dispatch(addNotification({
        type: 'success',
        message: 'Password changed successfully'
      }));
    } catch (error) {
      console.error('Failed to change password:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to change password'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleOrgUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.put(`${API_URL}/organizations/current`, orgData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      dispatch(addNotification({
        type: 'success',
        message: 'Organization updated successfully'
      }));
    } catch (error) {
      console.error('Failed to update organization:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to update organization'
      }));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profileData.firstName) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/chat')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Chat
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          My Profile
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your profile information and settings
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'profile'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'password'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            Change Password
          </button>
          <button
            onClick={() => setActiveTab('social')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'social'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            Social Links
          </button>
          {user?.role === 'ORG_ADMIN' && (
            <button
              onClick={() => setActiveTab('organization')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'organization'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Organization Information
            </button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
            <button
              onClick={() => setActiveTab('scripts')}
              className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'scripts'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Run script
            </button>
          )}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handleProfileUpdate}>
            {/* Profile Picture */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Profile Picture
              </label>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <PresignedAvatar
                    fileKey={profileData.profilePicture}
                    name={`${profileData.firstName || ''} ${profileData.lastName || ''}`}
                    initials={`${profileData.firstName?.[0] || ''}${profileData.lastName?.[0] || ''}`}
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    fallbackClassName="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700 text-white text-2xl font-medium"
                  />
                  <label className="absolute bottom-0 right-0 bg-primary-600 text-white rounded-full p-2 cursor-pointer hover:bg-primary-700">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click the camera icon to upload a new profile picture
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    JPG, PNG or GIF. Max size 5MB
                  </p>
                </div>
              </div>
              {pictureMessage && (
                <p className={`mt-2 text-sm ${pictureMessage.type === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {pictureMessage.text}
                </p>
              )}
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date of birth
              </label>
              {profileData.role === 'ORG_ADMIN' || profileData.role === 'ADMIN' ? (
                <input
                  id="dateOfBirth"
                  type="date"
                  value={profileData.dateOfBirth || ''}
                  onChange={(e) => setProfileData({ ...profileData, dateOfBirth: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              ) : (
                <div className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-white text-sm">
                  {formatDateOfBirth(profileData.dateOfBirth) || <span className="text-gray-500 dark:text-gray-400">Not set</span>}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-white text-sm">
                {profileData.email || <span className="text-gray-500 dark:text-gray-400">Not set</span>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact details
              </label>
              <div className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-white text-sm space-y-1">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Phone</span>
                  {profileData.contactNumber || <span className="text-gray-500 dark:text-gray-400">Not set</span>}
                </div>
                {profileData.username ? (
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Username</span>
                    @{profileData.username}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Role
              </label>
              <div className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-white text-sm">
                {profileData.organizationRoleName
                  || (profileData.role === 'ORG_ADMIN' ? 'Org admin' : '')
                  || (profileData.role === 'ADMIN' ? 'Platform admin' : '')
                  || (profileData.role === 'MANAGER' ? 'Manager' : '')
                  || <span className="text-gray-500 dark:text-gray-400">Not assigned</span>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Supervisor
              </label>
              <div className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-900 dark:text-white text-sm">
                {profileData.supervisorName ? (
                  <>
                    {profileData.supervisorName}
                    {profileData.supervisorUsername ? (
                      <span className="text-gray-500 dark:text-gray-400"> @{profileData.supervisorUsername}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">Not assigned</span>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Members under
              </label>
              <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 divide-y divide-gray-200 dark:divide-gray-700">
                {(profileData.membersUnder || []).length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No members report to you
                  </div>
                ) : (
                  profileData.membersUnder.map((member) => (
                    <div key={member.id} className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {member.name || member.username}
                      {member.username ? (
                        <span className="text-gray-500 dark:text-gray-400"> @{member.username}</span>
                      ) : null}
                      {member.organizationRoleName ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{member.organizationRoleName}</div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Social Links Tab */}
      {activeTab === 'social' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handleProfileUpdate}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* WhatsApp */}
              <div>
                <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WhatsApp Number/Link
                </label>
                <input
                  id="whatsapp"
                  type="text"
                  value={profileData.socialLinks.whatsapp}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, whatsapp: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g. +1234567890"
                />
              </div>

              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Facebook Profile Link
                </label>
                <input
                  id="facebook"
                  type="url"
                  value={profileData.socialLinks.facebook}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, facebook: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://facebook.com/username"
                />
              </div>

              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Instagram Profile Link
                </label>
                <input
                  id="instagram"
                  type="url"
                  value={profileData.socialLinks.instagram}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, instagram: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://instagram.com/username"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  LinkedIn Profile Link
                </label>
                <input
                  id="linkedin"
                  type="url"
                  value={profileData.socialLinks.linkedin}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, linkedin: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              {/* Zoom */}
              <div>
                <label htmlFor="zoom" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Zoom Meeting Link
                </label>
                <input
                  id="zoom"
                  type="url"
                  value={profileData.socialLinks.zoom}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, zoom: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://zoom.us/j/meeting-id"
                />
              </div>

              {/* Google Meet */}
              <div>
                <label htmlFor="googleMeet" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Google Meet Link
                </label>
                <input
                  id="googleMeet"
                  type="url"
                  value={profileData.socialLinks.googleMeet}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, googleMeet: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://meet.google.com/abc-defg-hij"
                />
              </div>

              {/* GitHub */}
              <div>
                <label htmlFor="github" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  GitHub Profile Link
                </label>
                <input
                  id="github"
                  type="url"
                  value={profileData.socialLinks.github}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, github: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://github.com/username"
                />
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Personal Website/Portfolio
                </label>
                <input
                  id="website"
                  type="url"
                  value={profileData.socialLinks.website}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialLinks: { ...profileData.socialLinks, website: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            {/* Custom Links Section */}
            <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Other Links</h3>
                <button
                  type="button"
                  onClick={handleAddCustomLink}
                  className="flex items-center text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Link
                </button>
              </div>

              {(!profileData.socialLinks.customLinks || profileData.socialLinks.customLinks.length === 0) ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No other links added yet.</p>
              ) : (
                <div className="space-y-4">
                  {profileData.socialLinks.customLinks.map((link, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={link.title}
                          onChange={(e) => handleUpdateCustomLink(index, 'title', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Title (e.g. Twitter, Portfolio)"
                          required
                        />
                        <input
                          type="url"
                          value={link.url}
                          onChange={(e) => handleUpdateCustomLink(index, 'url', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="URL (e.g. https://twitter.com/username)"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomLink(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors mt-1"
                        title="Remove link"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : 'Save Social Links'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handlePasswordChange}>
            <div className="mb-4">
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Password must be at least 6 characters long
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                minLength={6}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Organization Tab */}
      {activeTab === 'organization' && user?.role === 'ORG_ADMIN' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handleOrgUpdate}>
            <div className="mb-4">
              <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization Name
              </label>
              <input
                id="organizationName"
                type="text"
                value={orgData.organizationName}
                onChange={(e) => setOrgData({ ...orgData, organizationName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                minLength={2}
                maxLength={100}
              />
            </div>

            <div className="mb-6">
              <label htmlFor="organizationSlug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization Slug (Unique Identifier)
              </label>
              <input
                id="organizationSlug"
                type="text"
                value={orgData.organizationSlug}
                onChange={(e) => setOrgData({ ...orgData, organizationSlug: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                minLength={2}
                maxLength={50}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This is the unique identifier for your organization (e.g., used in URLs).
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="jiraSiteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization Jira URL
              </label>
              <input
                id="jiraSiteUrl"
                type="url"
                value={orgData.jiraSiteUrl}
                onChange={(e) => setOrgData({ ...orgData, jiraSiteUrl: e.target.value })}
                placeholder="https://your-company.atlassian.net"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Members only need their Jira email and API token after this is saved.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : 'Save Organization'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'scripts' && (user?.role === 'ADMIN' || user?.role === 'ORG_ADMIN') && (
        <AdminRunScriptPanel />
      )}
    </div>
  );
};

export default Profile;

